import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Loader2 } from "lucide-react";
import { Overline } from "./Motifs";
import { ARTICLES, SITE } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function Articles() {
  const [category, setCategory] = useState("All");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usedFallback, setUsedFallback] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/medium-feed`, { timeout: 12000 });
        if (!alive) return;
        if (data?.items?.length) {
          setItems(
            data.items.map((it) => ({
              title: it.title,
              category: (it.categories && it.categories[0]) || "Writing",
              excerpt: it.excerpt,
              minutes: it.minutes,
              link: it.link,
              date: it.pub_date,
            }))
          );
        } else {
          setUsedFallback(true);
          setItems(
            ARTICLES.map((a) => ({ ...a, link: SITE.socials.medium, date: null }))
          );
        }
      } catch (e) {
        if (!alive) return;
        setUsedFallback(true);
        setItems(ARTICLES.map((a) => ({ ...a, link: SITE.socials.medium, date: null })));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // Mount-only fetch with alive-guard; intentionally not re-running on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [items]);

  const filtered =
    category === "All" ? items : items.filter((a) => a.category === category);

  return (
    <section
      id="articles"
      data-testid="articles-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Overline>Articles & Reflections</Overline>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1E2A4F] mt-5 leading-[0.95] tracking-tight">
              On creativity, play,
              <br />
              <em className="italic font-light">and the experiment of living.</em>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#5C6A8F] leading-relaxed">
              Long-form writing pulled live from{" "}
              <a
                href={SITE.socials.medium}
                target="_blank"
                rel="noreferrer"
                className="text-[#1E2A4F] ink-link"
              >
                medium.com/@divyaramachandran
              </a>{" "}
              — classrooms, kitchens, and the occasional 4 a.m. epiphany.
            </p>
            <a
              href={SITE.socials.medium}
              target="_blank"
              rel="noreferrer"
              data-testid="articles-medium-link"
              className="mt-5 inline-flex items-center gap-2 text-sm text-[#D95D39] ink-link"
            >
              Read everything on Medium <ArrowUpRight size={14} />
            </a>
          </div>
        </div>

        {/* Category filter */}
        <div className="mt-14 flex flex-wrap gap-2 sm:gap-3 border-b border-[#E5DFD3] pb-6">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`articles-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-xs tracking-[0.18em] uppercase transition-all duration-500 ${
                category === c
                  ? "bg-[#1E2A4F] text-[#F9F6F0]"
                  : "text-[#5C6A8F] hover:text-[#1E2A4F]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Articles grid */}
        {loading ? (
          <div className="mt-16 flex items-center gap-3 text-[#5C6A8F]" data-testid="articles-loading">
            <Loader2 size={18} className="animate-spin" /> Fetching latest from Medium…
          </div>
        ) : (
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((a, i) => (
              <motion.a
                key={`${a.title}-${i}`}
                href={a.link || SITE.socials.medium}
                target="_blank"
                rel="noreferrer"
                data-testid={`article-card-${i}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="group p-7 rounded-lg border border-[#E5DFD3] bg-white hover:border-[#1E2A4F] hover:-translate-y-1 transition-all duration-500 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] tracking-[0.24em] uppercase text-[#D95D39] font-medium">
                    {a.category}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-[#5C6A8F]">
                    <Clock size={12} /> {a.minutes} min
                  </span>
                </div>
                <h3 className="font-display text-2xl text-[#1E2A4F] mt-4 leading-snug group-hover:text-[#D95D39] transition-colors duration-500">
                  {a.title}
                </h3>
                <p className="text-sm text-[#5C6A8F] mt-3 leading-relaxed flex-1">{a.excerpt}</p>
                <div className="mt-6 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#1E2A4F]">
                    Read on Medium <ArrowUpRight size={12} />
                  </span>
                  {a.date && (
                    <span className="text-xs text-[#5C6A8F]">{formatDate(a.date)}</span>
                  )}
                </div>
              </motion.a>
            ))}
          </div>
        )}

        {usedFallback && !loading && (
          <p className="mt-8 text-xs text-[#5C6A8F]" data-testid="articles-fallback-note">
            (Could not reach Medium right now — showing curated samples. Live feed will return automatically.)
          </p>
        )}
      </div>
    </section>
  );
}
